import { ActionIcon, Box, Button, Group, Modal, NumberInput, Paper, Select, SimpleGrid, Stack, Table, Text, Textarea, Tooltip } from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { IconCalendar, IconPencil, IconRefresh, IconTrash } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { api } from '../api/client';
import type { Health, OrganizationDetails, Plan, SubscriptionAccess, SubscriptionPayment, SubscriptionStatus } from '../api/types';
import { isEn, nameOf, tr } from '../lib/i18n';
import { fmtDay, fmtDaysLeft, fmtDuration, fmtMoney, HEALTH, PAYMENT_METHOD, SUBSCRIPTION_STATUS } from '../lib/labels';
import { ConfirmModal, EmptyState, Panel, StatusBadge, toneOf, type Tone } from '../ui/kit';
import { useToast } from './Toast';

export const usePlans = () => useQuery({ queryKey: ['plans'], queryFn: () => api<{ items: Plan[] }>('/plans') });

/** What the company experiences right now (shown next to the subscription status). */
export const ACCESS_HINT: Record<string, string> = {
  GRACE: tr('في مهلة السماح — الشركة تعمل طبيعياً وترى تحذيراً', 'In grace period — the company works normally and sees a warning'),
  LOCKED: tr('قراءة فقط — الشركة لا تستطيع الإضافة أو التعديل، والنموذج العام متوقف', 'Read-only — the company cannot add or edit, and the public form is disabled'),
};

export function SubscriptionChip({ status, daysLeft, planName, access }: { status: SubscriptionStatus; daysLeft: number | null; planName?: string | null; access?: SubscriptionAccess }) {
  const s = SUBSCRIPTION_STATUS[status] ?? SUBSCRIPTION_STATUS.NONE!;
  const state = access === 'LOCKED' ? tr('قراءة فقط', 'Read-only') : access === 'GRACE' ? tr(`مهلة · ${fmtDaysLeft(daysLeft)}`, `Grace · ${fmtDaysLeft(daysLeft)}`) : status === 'ACTIVE' ? s.label : fmtDaysLeft(daysLeft);
  const label = status === 'NONE' ? s.label : `${planName ? `${planName} · ` : ''}${state}`;
  const tip = access && ACCESS_HINT[access] ? ACCESS_HINT[access] : status === 'NONE' ? '' : fmtDaysLeft(daysLeft);
  return (
    <Tooltip label={tip} disabled={!tip} multiline maw={260}>
      <span><StatusBadge tone={access === 'LOCKED' ? 'red' : toneOf(s.color)}>{label}</StatusBadge></span>
    </Tooltip>
  );
}

const HEALTH_TONE: Record<string, Tone> = { ACTIVE: 'green', COOLING: 'orange', SLEEPING: 'red', NEW: 'gray' };

export function HealthDot({ health, withLabel }: { health: Health | null | undefined; withLabel?: boolean }) {
  if (!health) return null;
  const h = HEALTH[health]!;
  return (
    <Tooltip label={h.hint} multiline maw={260}>
      <Group gap={6} wrap="nowrap" component="span" style={{ display: 'inline-flex' }}>
        <Box component="span" w={8} h={8} style={{ borderRadius: 99, background: `var(--mantine-color-${HEALTH_TONE[health] ?? 'gray'}-6)`, flexShrink: 0 }} />
        {withLabel && <Text size="sm" component="span">{h.label}</Text>}
      </Group>
    </Tooltip>
  );
}

const addDays = (iso: string, n: number) => {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
};
export const today = () => new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Amman' }).format(new Date());

/** Same rule as the API: end (inclusive) = start + duration − 1 day (months clamp to month end). */
export function previewEnd(start: string, p: Plan): string {
  const d = new Date(`${start}T00:00:00Z`);
  if (p.duration_unit === 'MONTH') {
    const day = d.getUTCDate();
    d.setUTCDate(1);
    d.setUTCMonth(d.getUTCMonth() + p.duration_count);
    const last = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0)).getUTCDate();
    d.setUTCDate(Math.min(day, last));
  } else d.setUTCDate(d.getUTCDate() + p.duration_count);
  d.setUTCDate(d.getUTCDate() - 1);
  return d.toISOString().slice(0, 10);
}

const arrow = () => (isEn() ? '→' : '←');

/** Record a payment and extend the subscription. */
export function RenewDialog({ open, onClose, org }: { open: boolean; onClose: () => void; org: OrganizationDetails }) {
  const qc = useQueryClient();
  const { toast, toastError } = useToast();
  const plans = usePlans();
  const active = (plans.data?.items ?? []).filter((p) => p.is_active);
  const [planId, setPlanId] = useState<number | ''>('');
  const [amount, setAmount] = useState<number | ''>('');
  const [method, setMethod] = useState('CASH');
  const [note, setNote] = useState('');
  const plan = active.find((p) => p.id === planId);

  useEffect(() => {
    if (!open) return;
    const initial = active.find((p) => p.id === org.plan_id && p.price !== '0.00') ?? active.find((p) => Number(p.price) > 0) ?? active[0];
    setPlanId(initial?.id ?? '');
    setAmount(initial ? Number(initial.price) : '');
    setMethod('CASH');
    setNote('');
  }, [open, plans.data]); // eslint-disable-line react-hooks/exhaustive-deps

  const start = org.subscription_ends_at && org.subscription_ends_at >= today() ? addDays(org.subscription_ends_at, 1) : today();
  const end = plan ? previewEnd(start, plan) : null;

  const renew = useMutation({
    mutationFn: () => api(`/organizations/${org.id}/subscription/renew`, { method: 'POST', body: { planId, recordPayment: Number(plan?.price ?? 0) > 0 || Number(amount) > 0, amount: Number(amount), method, ...(note.trim() ? { note: note.trim() } : {}) } }),
    onSuccess: () => {
      toast(tr(`تم التجديد حتى ${fmtDay(end)}`, `Renewed until ${fmtDay(end)}`));
      for (const k of [['organization', org.id], ['payments', org.id], ['organizations'], ['dashboard'], ['plans']]) void qc.invalidateQueries({ queryKey: k });
      onClose();
    },
    onError: toastError,
  });

  return (
    <Modal opened={open} onClose={onClose} title={tr('تجديد الاشتراك وتسجيل دفعة', 'Renew subscription and record payment')} size="md">
      <Stack gap="md">
        <Select
          label={tr('الباقة', 'Plan')}
          data={active.map((p) => ({ value: String(p.id), label: `${nameOf(p)} — ${fmtMoney(p.price, p.currency)} / ${fmtDuration(p.duration_count, p.duration_unit)}` }))}
          value={planId === '' ? null : String(planId)}
          onChange={(v) => {
            const p = active.find((x) => x.id === Number(v));
            setPlanId(p?.id ?? '');
            if (p) setAmount(Number(p.price));
          }}
        />
        <SimpleGrid cols={2} spacing="sm">
          <NumberInput label={tr('المبلغ المدفوع', 'Amount paid')} value={amount} onChange={(v) => setAmount(v === '' ? '' : Number(v))} min={0} description={tr('يمكنك تعديله (خصم مثلاً)', 'Adjustable (e.g. a discount)')} />
          <Select label={tr('طريقة الدفع', 'Payment method')} data={Object.entries(PAYMENT_METHOD).map(([value, label]) => ({ value, label }))} value={method} onChange={(v) => setMethod(v ?? 'CASH')} />
        </SimpleGrid>
        <Textarea label={tr('ملاحظة (اختياري)', 'Note (optional)')} value={note} onChange={(e) => setNote(e.currentTarget.value)} />
        {end && (
          <Paper withBorder p="sm" radius="md" bg="var(--pf-hover)">
            <Text size="sm">{tr('الفترة الجديدة:', 'New period:')} <b>{fmtDay(start)}</b> {arrow()} <b>{fmtDay(end)}</b></Text>
            {org.subscription_ends_at && org.subscription_ends_at >= today() && (
              <Text size="xs" c="dimmed" mt={2}>{tr('تبدأ بعد نهاية الاشتراك الحالي، فلا يضيع على الشركة أي يوم.', 'It starts after the current subscription ends, so the company loses no days.')}</Text>
            )}
          </Paper>
        )}
        <Group justify="flex-end" gap="xs">
          <Button variant="default" onClick={onClose}>{tr('إلغاء', 'Cancel')}</Button>
          <Button onClick={() => renew.mutate()} loading={renew.isPending} disabled={!plan || amount === '' || Number(amount) < 0}>{tr('تجديد', 'Renew')}</Button>
        </Group>
      </Stack>
    </Modal>
  );
}

/** Manual set: trial, corrections, cancel — no payment is recorded. */
export function SetSubscriptionDialog({ open, onClose, org }: { open: boolean; onClose: () => void; org: OrganizationDetails }) {
  const qc = useQueryClient();
  const { toast, toastError } = useToast();
  const plans = usePlans();
  const [planId, setPlanId] = useState<number | ''>('');
  const [endsAt, setEndsAt] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setPlanId(org.plan_id ?? '');
    setEndsAt(org.subscription_ends_at ?? null);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = useMutation({
    mutationFn: (body: { planId: number | null; endsAt: string | null }) => api(`/organizations/${org.id}/subscription`, { method: 'PUT', body }),
    onSuccess: (_, body) => {
      toast(body.planId ? tr('تم حفظ الاشتراك', 'Subscription saved') : tr('تم إلغاء الاشتراك', 'Subscription cancelled'));
      for (const k of [['organization', org.id], ['organizations'], ['dashboard']]) void qc.invalidateQueries({ queryKey: k });
      onClose();
    },
    onError: toastError,
  });

  const pickPlan = (id: number) => {
    setPlanId(id);
    const p = plans.data?.items.find((x) => x.id === id);
    if (p && !endsAt) setEndsAt(previewEnd(today(), p));
  };

  return (
    <Modal opened={open} onClose={onClose} title={tr('تعديل الاشتراك يدوياً', 'Edit subscription manually')} size="sm">
      <Stack gap="md">
        <Text size="sm" c="dimmed">
          {tr('للفترة التجريبية أو لتصحيح خطأ — لا تُسجَّل دفعة. للتجديد المدفوع استخدم «تجديد».', 'For a trial period or to fix a mistake — no payment is recorded. For a paid renewal, use “Renew”.')}
        </Text>
        <Select
          label={tr('الباقة', 'Plan')}
          data={(plans.data?.items ?? []).map((p) => ({ value: String(p.id), label: `${nameOf(p)}${p.is_active ? '' : tr(' (موقوفة)', ' (inactive)')}` }))}
          value={planId === '' ? null : String(planId)}
          onChange={(v) => v && pickPlan(Number(v))}
        />
        <DatePickerInput label={tr('ينتهي في (شامل)', 'Ends on (inclusive)')} value={endsAt} onChange={(v) => setEndsAt(v)} valueFormat="YYYY-MM-DD" leftSection={<IconCalendar size={16} />} />
        <Group justify="space-between" gap="xs">
          <Button variant="subtle" color="red" onClick={() => save.mutate({ planId: null, endsAt: null })} disabled={!org.plan_id || save.isPending}>{tr('إلغاء الاشتراك', 'Cancel subscription')}</Button>
          <Group gap="xs">
            <Button variant="default" onClick={onClose}>{tr('إغلاق', 'Close')}</Button>
            <Button loading={save.isPending} disabled={planId === '' || !endsAt} onClick={() => save.mutate({ planId: Number(planId), endsAt })}>{tr('حفظ', 'Save')}</Button>
          </Group>
        </Group>
      </Stack>
    </Modal>
  );
}

function Stat({ label, children }: { label: string; children: ReactNode }) {
  return (
    <Box>
      <Text size="xs" c="dimmed" mb={4}>{label}</Text>
      <Box fz="sm" fw={600}>{children}</Box>
    </Box>
  );
}

/** Subscription panel + payments history on the company page. */
export function SubscriptionCard({ org }: { org: OrganizationDetails }) {
  const qc = useQueryClient();
  const { toast, toastError } = useToast();
  const [renewing, setRenewing] = useState(false);
  const [editing, setEditing] = useState(false);
  const [deleting, setDeleting] = useState<SubscriptionPayment | null>(null);
  const payments = useQuery({ queryKey: ['payments', org.id], queryFn: () => api<{ items: SubscriptionPayment[] }>(`/organizations/${org.id}/payments`) });
  const total = useMemo(() => (payments.data?.items ?? []).reduce((s, p) => s + Number(p.amount), 0), [payments.data]);

  const del = useMutation({
    mutationFn: (p: SubscriptionPayment) => api(`/organizations/${org.id}/payments/${p.id}`, { method: 'DELETE' }),
    onSuccess: () => {
      toast(tr('تم حذف الدفعة — راجع تاريخ الانتهاء إن لزم', 'Payment deleted — review the end date if needed'));
      setDeleting(null);
      void qc.invalidateQueries({ queryKey: ['payments', org.id] });
      void qc.invalidateQueries({ queryKey: ['dashboard'] });
    },
    onError: toastError,
  });

  const s = SUBSCRIPTION_STATUS[org.subscriptionStatus]!;
  const items = payments.data?.items ?? [];
  return (
    <Panel
      title={tr('الاشتراك', 'Subscription')}
      actions={
        <>
          <Button variant="default" leftSection={<IconPencil size={16} />} onClick={() => setEditing(true)}>{tr('تعديل يدوي', 'Edit manually')}</Button>
          <Button leftSection={<IconRefresh size={16} />} onClick={() => setRenewing(true)}>{tr('تجديد', 'Renew')}</Button>
        </>
      }
    >
      <SimpleGrid cols={{ base: 2, md: 4 }} spacing="lg" mb="lg">
        <Stat label={tr('الباقة', 'Plan')}>{org.plan ? `${nameOf(org.plan)} — ${fmtMoney(org.plan.price, org.plan.currency)} / ${fmtDuration(org.plan.duration_count, org.plan.duration_unit)}` : '—'}</Stat>
        <Stat label={tr('ينتهي في', 'Ends on')}>{fmtDay(org.subscription_ends_at)}</Stat>
        <Stat label={tr('الحالة', 'Status')}>
          <StatusBadge tone={org.access === 'LOCKED' ? 'red' : toneOf(s.color)}>{org.subscriptionStatus === 'NONE' ? s.label : `${s.label} · ${fmtDaysLeft(org.daysLeft)}`}</StatusBadge>
          {org.access && ACCESS_HINT[org.access] && (
            <Text size="xs" fw={400} c={org.access === 'LOCKED' ? 'red.7' : 'dimmed'} mt={4} maw={260}>
              {ACCESS_HINT[org.access]}{org.access === 'GRACE' && org.graceEndsAt ? tr(` حتى ${fmtDay(org.graceEndsAt)}`, ` until ${fmtDay(org.graceEndsAt)}`) : ''}
            </Text>
          )}
        </Stat>
        <Stat label={tr('مجموع ما دفعته الشركة', 'Total paid')}><span className="pf-num">{fmtMoney(total)}</span></Stat>
      </SimpleGrid>

      <Text size="sm" fw={600} mb="xs">{tr('سجل الدفعات', 'Payment history')}</Text>
      {items.length === 0 ? (
        <Paper withBorder radius="md"><EmptyState title={tr('لا توجد دفعات بعد', 'No payments yet')} /></Paper>
      ) : (
        <Paper withBorder radius="md" style={{ overflow: 'hidden' }}>
          <Table.ScrollContainer minWidth={640}>
            <Table fz="sm" verticalSpacing={8}>
              <Table.Thead bg="#fafbfc">
                <Table.Tr>
                  {[tr('التاريخ', 'Date'), tr('الباقة', 'Plan'), tr('المبلغ', 'Amount'), tr('الطريقة', 'Method'), tr('الفترة', 'Period'), tr('سجّلها', 'Recorded by'), ''].map((h, i) => (
                    <Table.Th key={i} c="dimmed" fw={500} fz={12.5}>{h}</Table.Th>
                  ))}
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {items.map((p) => (
                  <Table.Tr key={p.id}>
                    <Table.Td>{fmtDay(p.created_at)}</Table.Td>
                    <Table.Td>{p.plan_name}</Table.Td>
                    <Table.Td className="pf-num" fw={500}>{fmtMoney(p.amount, p.currency)}</Table.Td>
                    <Table.Td>{PAYMENT_METHOD[p.method]}</Table.Td>
                    <Table.Td c="dimmed">{fmtDay(p.period_start)} {arrow()} {fmtDay(p.period_end)}</Table.Td>
                    <Table.Td>{p.recorded_by_name ?? '—'}{p.note ? <Text size="xs" c="dimmed">{p.note}</Text> : null}</Table.Td>
                    <Table.Td w={44}>
                      <Tooltip label={tr('حذف (دفعة مسجلة بالخطأ)', 'Delete (recorded by mistake)')}>
                        <ActionIcon color="red" onClick={() => setDeleting(p)} aria-label={tr('حذف', 'Delete')}><IconTrash size={16} /></ActionIcon>
                      </Tooltip>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        </Paper>
      )}
      <RenewDialog open={renewing} onClose={() => setRenewing(false)} org={org} />
      <SetSubscriptionDialog open={editing} onClose={() => setEditing(false)} org={org} />
      <ConfirmModal
        open={!!deleting}
        danger
        title={tr('حذف الدفعة', 'Delete payment')}
        busy={del.isPending}
        text={tr('تُحذف الدفعة من السجل والإيرادات فقط — تاريخ انتهاء الاشتراك لا يتغير، عدّله يدوياً إن لزم.', 'The payment is removed from the history and revenue only — the subscription end date does not change; edit it manually if needed.')}
        onClose={() => setDeleting(null)}
        onConfirm={() => deleting && del.mutate(deleting)}
        confirmLabel={tr('حذف', 'Delete')}
      />
    </Panel>
  );
}
